import {
  ConcatText,
  Icon,
  Loading,
  Header,
  Text,
  ProfileBiography,
  PostList,
  Avatar,
} from 'components';
import { useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { TAdminPosterDetailResponse } from 'types';
import { PAGE_SIZE, TEXT_STRING, queryKeys } from '../../constants';
import { getAdminPosterDetail, getAdminPosterPosts } from 'api';
import { useParams } from 'react-router-dom';
import { detectMobile } from 'utils';
import { Waypoint } from 'react-waypoint';
import InfiniteScroll from 'react-infinite-scroll-component';
import LoadingPost from 'components/PostItem/LoadingPost';

const COMMON_STR = TEXT_STRING.COMMON;

function DetailAdminPoster() {
  // Hooks
  const { id } = useParams();
  const isMobile = detectMobile();

  // States
  const [viewScroll, setViewScroll] = useState<boolean>(true);

  // Query
  const { data, isLoading } = useQuery<TAdminPosterDetailResponse>({
    queryKey: [queryKeys.ADMIN_POSTER_DETAIL, id],
    queryFn: () => getAdminPosterDetail(+(id || '')),
    enabled: !!id,
  });

  const {
    data: postData,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingPost,
  } = useInfiniteQuery({
    queryKey: [queryKeys.ADMIN_POSTER_POSTS, id],
    queryFn: async ({ pageParam }) =>
      getAdminPosterPosts({
        adminPosterId: +(id || 0),
        currentPage: pageParam,
        pageSize: PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, currentPage) => {
      return lastPage?.totalPage > currentPage ? currentPage + 1 : undefined;
    },
    enabled: !!id,
  });

  const postListView = useMemo(() => {
    return postData?.pages.map(({ data }) => data).flat() ?? [];
  }, [postData]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="h-full">
      <Header
        title={
          <div className="flex items-center justify-center line-clamp-1 gap-4">
            <ConcatText
              prefixText={data?.nickname}
              suffixText={data?.id}
              className="font-bold line-clamp-1"
            />
          </div>
        }
      />
      {data ? (
        <div className="h-full overflow-hidden pb-16">
          <InfiniteScroll
            next={fetchNextPage}
            hasMore={hasNextPage}
            loader={<LoadingPost />}
            dataLength={postListView.length}
            scrollThreshold={0.5}
            height={'calc(100dvh - 68px - 46px)'}
          >
            <div className="overflow-hidden">
              <Avatar url={data?.avatar} className="object-contain" />
              {/* Profile */}
              <Waypoint
                onEnter={() => {
                  setViewScroll(false);
                }}
                onLeave={() => {
                  setViewScroll(true);
                }}
                bottomOffset={'50%'}
              >
                <div className="px-4 pb-28">
                  <div className="pt-12px">
                    <ProfileBiography note={data?.biography} />
                  </div>
                  {/* List post */}
                  <>
                    <hr className="solid my-8" />
                    <Text textColor="text-primary" bold fontSize={14} center>
                      {COMMON_STR.RECENT_TWEETS}
                    </Text>
                    <hr className="solid my-8" />
                    {isLoadingPost ? (
                      <LoadingPost />
                    ) : postListView?.length ? (
                      <PostList data={postListView} />
                    ) : (
                      <Text center bold fontSize={16} className="mt-4">
                        {COMMON_STR.NO_DATA}
                      </Text>
                    )}
                  </>
                </div>
              </Waypoint>
            </div>
          </InfiniteScroll>
          <div
            className={`sticky bottom-0 ${
              isMobile ? 'w-full' : 'w-[50vh]'
            } p-12px mx-auto z-[2]`}
          >
            {viewScroll && (
              <div className="absolute -top-9 bg-black/50 left-1/2 -translate-x-1/2 py-2 px-10 backdrop-blur-sm rounded-full">
                <Icon icon="chevronDownDouble" />
              </div>
            )}
          </div>
        </div>
      ) : (
        <Text center bold fontSize={16} className="mt-4">
          {COMMON_STR.NO_DATA}
        </Text>
      )}
    </div>
  );
}

export default DetailAdminPoster;
